import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAuditLog } from '@/hooks/useAuditLog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CompanyUsageStats {
  companyId: string;
  lastUsed: string;
  useCount: number;
  favoritePriority: number;
}

export const useCompanyContext = () => {
  const { 
    userCompanies, 
    selectedCompany, 
    selectCompany: authSelectCompany,
    user 
  } = useAuth();
  const { logCompanySwitch } = useAuditLog();
  const { toast } = useToast();
  
  const [companyStats, setCompanyStats] = useState<CompanyUsageStats[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  // Cargar estadísticas de uso de empresas
  useEffect(() => {
    if (user) {
      loadCompanyUsageStats();
    }
  }, [user]);

  const loadCompanyUsageStats = useCallback(async () => {
    if (!user) return;

    try {
      const statsKey = `company_stats_${user.id}`;
      const saved = localStorage.getItem(statsKey);
      if (saved) {
        setCompanyStats(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading company stats:', error);
    }
  }, [user]);

  const updateCompanyStats = useCallback(async (companyId: string) => {
    if (!user) return;

    const statsKey = `company_stats_${user.id}`;
    const now = new Date().toISOString();
    
    setCompanyStats(prevStats => {
      const updatedStats = prevStats.filter(stat => stat.companyId !== companyId);
      updatedStats.unshift({
        companyId,
        lastUsed: now,
        useCount: (prevStats.find(s => s.companyId === companyId)?.useCount || 0) + 1,
        favoritePriority: 0
      });

      // Mantener solo las últimas 10 empresas
      const finalStats = updatedStats.slice(0, 10);
      
      try {
        localStorage.setItem(statsKey, JSON.stringify(finalStats));
      } catch (error) {
        console.error('Error saving company stats:', error);
      }

      return finalStats;
    });
  }, [user]);

  // Validar permisos de empresa
  const validateCompanyPermissions = useCallback(async (companyId: string) => {
    if (!user) return false;

    setIsValidating(true);
    try {
      // 🔐 VERIFICAR SI ES SUPER ADMIN PRIMERO
      // Los super admins pueden acceder a cualquier empresa
      const { data: superAdminCheck, error: superAdminError } = await supabase
        .from('user_roles')
        .select('role:roles(code)')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (!superAdminError && superAdminCheck) {
        const isSuperAdmin = superAdminCheck.some(ur => ur.role?.code === 'super_admin');
        if (isSuperAdmin) {
          console.log('🔐 Super admin detected - allowing access to company:', companyId);
          return true;
        }
      }

      // Para usuarios regulares, verificar rol específico en la empresa
      const { data, error } = await supabase
        .from('user_roles')
        .select('id, is_active')
        .eq('user_id', user.id)
        .eq('company_id', companyId)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        toast({
          title: "Acceso Denegado",
          description: "No tienes permisos para acceder a esta empresa.",
          variant: "destructive",
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error validating permissions:', error);
      return false;
    } finally {
      setIsValidating(false);
    }
  }, [user, toast]);

  // Función mejorada para cambiar empresa
  const switchCompany = useCallback(async (company: typeof selectedCompany) => {
    if (!company || !user) return;
    
    // Evitar logs duplicados - solo loggear si realmente hay un cambio
    if (selectedCompany?.id === company.id) {
      console.log('🔄 Empresa ya seleccionada, no es necesario cambiar:', company.name);
      return;
    }

    // Validar permisos
    const hasPermission = await validateCompanyPermissions(company.id);
    if (!hasPermission) return;

    // Log del cambio SOLO si hay cambio real
    console.log('🏢 Registrando cambio de empresa:', {
      desde: selectedCompany?.name || 'ninguna',
      hacia: company.name
    });
    
    await logCompanySwitch(selectedCompany, company);

    // Cambiar empresa
    authSelectCompany(company);

    // Actualizar estadísticas
    await updateCompanyStats(company.id);

    toast({
      title: "Empresa Cambiada",
      description: `Ahora operando como: ${company.name}`,
      duration: 2000,
    });
  }, [
    selectedCompany,
    user,
    validateCompanyPermissions,
    logCompanySwitch,
    authSelectCompany,
    updateCompanyStats,
    toast
  ]);

  // Obtener empresas frecuentes
  const getFrequentCompanies = useCallback(() => {
    return userCompanies
      .map(uc => ({
        ...uc.company,
        stats: companyStats.find(stat => stat.companyId === uc.company.id)
      }))
      .sort((a, b) => {
        // Priorizar por frecuencia de uso y recencia
        const aStats = a.stats;
        const bStats = b.stats;
        
        if (!aStats && !bStats) return 0;
        if (!aStats) return 1;
        if (!bStats) return -1;

        // Combinar recencia y frecuencia
        const aScore = aStats.useCount + (Date.now() - new Date(aStats.lastUsed).getTime()) / (1000 * 60 * 60 * 24 * 7);
        const bScore = bStats.useCount + (Date.now() - new Date(bStats.lastUsed).getTime()) / (1000 * 60 * 60 * 24 * 7);
        
        return bScore - aScore;
      })
      .slice(0, 5);
  }, [userCompanies, companyStats]);

  return {
    switchCompany,
    validateCompanyPermissions,
    getFrequentCompanies,
    companyStats,
    isValidating,
    totalCompanies: userCompanies.length,
    hasMultipleCompanies: userCompanies.length > 1
  };
};